/**
 * The desktop window's chrome, as reactive state and as classes / CSS
 * variables on `<html>`.
 *
 * The shell reports where its native chrome sits (title bar height, traffic
 * lights on macOS, caption buttons on Windows) and the window's state
 * (focused, fullscreen, translucent, offline). One module-level copy feeds
 * every reader; `applyChromeToDocument()` mirrors it onto the document so CSS
 * can lay out around the native controls without JS:
 *
 *   html.desktop-app                    always, inside the desktop app
 *   html.desktop-mac | -win | -linux     the OS
 *   html.desktop-translucent             vibrancy / Mica shows behind transparent areas
 *   html.desktop-fullscreen              traffic lights hidden, no title bar inset
 *   html.desktop-blurred                 the window is not key (dim the chrome like native apps)
 *   html.desktop-offline                 no network
 *   --desktop-titlebar-h, --desktop-inset-left, --desktop-inset-right   (px)
 *
 * index.html applies the same classes from `initialChrome` before first
 * paint, so there is no layout jump when this module takes over.
 */
import { computed, readonly, ref } from 'vue'
import { getDesktopBridge, toDesktopOs, type DesktopOs } from './bridge'
import type { ChromeMetrics, Unsubscribe } from './types'

const metrics = ref<ChromeMetrics | null>(null)
/** The page's own `offline` event (useConnectivity), which can land before the shell's next report. */
const browserOffline = ref(false)
let unsubscribe: Unsubscribe | null = null

/** Every class this module may set, so a stale one is always removed. */
export const DESKTOP_CHROME_CLASSES = [
    'desktop-app',
    'desktop-mac',
    'desktop-win',
    'desktop-linux',
    'desktop-translucent',
    'desktop-fullscreen',
    'desktop-blurred',
    'desktop-offline',
] as const

/** The `<html>` classes and CSS variables for a set of metrics (pure, for tests and the pre-paint script). */
export function chromeDocumentState(m: ChromeMetrics): { classes: string[]; vars: Record<string, string> } {
    const os: DesktopOs = toDesktopOs(m.platform)
    const classes: string[] = ['desktop-app', `desktop-${os}`]
    if (m.translucent) classes.push('desktop-translucent')
    if (m.fullscreen) classes.push('desktop-fullscreen')
    if (!m.focused) classes.push('desktop-blurred')
    if (m.offline) classes.push('desktop-offline')
    const px = (n: number) => `${Math.max(0, Math.round(Number(n) || 0))}px`
    return {
        classes,
        vars: {
            '--desktop-titlebar-h': px(m.titleBarHeight),
            '--desktop-inset-left': px(m.fullscreen ? 0 : m.insetLeft),
            '--desktop-inset-right': px(m.insetRight),
        },
    }
}

/** Mirror the metrics onto `<html>` (classes + CSS variables). */
export function applyChromeToDocument(m: ChromeMetrics | null = metrics.value, doc: Document = document): void {
    if (!m || !doc?.documentElement) return
    const root = doc.documentElement
    const { classes, vars } = chromeDocumentState(browserOffline.value ? { ...m, offline: true } : m)
    const want = new Set(classes)
    for (const cls of DESKTOP_CHROME_CLASSES) root.classList.toggle(cls, want.has(cls))
    for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value)
}

/**
 * Start following the shell's chrome (idempotent). Returns false outside the
 * desktop app. Called by `initDesktopShell`; components just read the state.
 */
export function startDesktopChrome(): boolean {
    const bridge = getDesktopBridge()
    if (!bridge) return false
    if (unsubscribe) return true
    if (bridge.initialChrome) metrics.value = { ...bridge.initialChrome }
    applyChromeToDocument()
    unsubscribe = bridge.chrome.onChange((next) => {
        metrics.value = { ...next }
        applyChromeToDocument()
    })
    // The initial snapshot is from window creation; ask once for the live values.
    bridge.chrome.get().then((live) => {
        if (!live) return
        metrics.value = { ...live }
        applyChromeToDocument()
    }).catch(() => {})
    return true
}

/** The browser's own online / offline signal, combined with the shell's (offline if either says so). */
export function setBrowserOffline(offline: boolean): void {
    if (browserOffline.value === offline) return
    browserOffline.value = offline
    applyChromeToDocument()
}

/** Test hook. */
export function resetDesktopChrome(): void {
    unsubscribe?.()
    unsubscribe = null
    metrics.value = null
    browserOffline.value = false
}

/**
 * Reactive chrome state for the title bar / toolbar and anything that lays
 * out around the native controls. All values are safe defaults outside the app.
 */
export function useDesktopChrome() {
    const bridge = getDesktopBridge()
    return {
        /** Raw metrics from the shell (null outside the desktop app). */
        metrics: readonly(metrics),
        isDesktop: computed(() => metrics.value !== null),
        os: computed<DesktopOs | null>(() => (metrics.value ? toDesktopOs(metrics.value.platform) : null)),
        titleBarHeight: computed(() => metrics.value?.titleBarHeight ?? 0),
        insetLeft: computed(() => (metrics.value && !metrics.value.fullscreen ? metrics.value.insetLeft : 0)),
        insetRight: computed(() => metrics.value?.insetRight ?? 0),
        focused: computed(() => metrics.value?.focused ?? true),
        fullscreen: computed(() => metrics.value?.fullscreen ?? false),
        maximized: computed(() => metrics.value?.maximized ?? false),
        translucent: computed(() => metrics.value?.translucent ?? false),
        /** Shell or browser reports no network (prefer `useConnectivity()` outside the chrome). */
        offline: computed(() => Boolean(metrics.value?.offline) || browserOffline.value),
        canGoBack: computed(() => metrics.value?.canGoBack ?? false),
        canGoForward: computed(() => metrics.value?.canGoForward ?? false),
        /** Toolbar back / forward: the shell owns the history so the native buttons and menu agree. */
        goBack: () => bridge?.chrome.goBack(),
        goForward: () => bridge?.chrome.goForward(),
        /** Windows / Linux: pop the File / Edit / View menu from a hamburger in the title bar. */
        showAppMenu: (point?: { x: number; y: number }) => bridge?.chrome.showAppMenu(point),
    }
}

export default useDesktopChrome
